import React from "react";

export const isEDMode =
  typeof window !== "undefined"
    ? window.location.href.indexOf("ed=1") > 0
    : false;

export const getUrlParameter = (param) => {
  param = param.replace(/[\\[]/, "\\[").replace(/[\]]/, "\\]");
  const regex = new RegExp("[\\?&]" + param + "=([^&#]*)");
  const results = regex.exec(window.location.search);

  return results === null
    ? ""
    : decodeURIComponent(results[1].replace(/\+/g, " "));
};

/**
 * Creates a debounced function that delays invoking the provided function
 * until after the specified delay has elapsed.
 *
 * IMPORTANT: To ensure proper timeout closure, the debounced function should be
 * declared outside of any component or function where it will be used.
 *
 * ✅ CORRECT USAGE:
 * const debouncedFunction = debounce((value) => {
 *   // do something with value
 * }, 300);
 * window.addEventListener("scroll", debouncedFunction);
 *
 * ❌ INCORRECT USAGE (Don't do this):
 * window.addEventListener("scroll", debounce(() => {
 *   // code
 * }, 200));
 *
 * @param {Function} func - The function to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} The debounced function
 */
export const debounce = (func, delay) => {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), delay);
  };
};

export const extractTextFromElement = (element) => {
  // Convert element to string first
  let text = "";

  if (typeof element === "string") {
    text = element;
  } else if (React.isValidElement(element)) {
    const children = element.props?.children;
    if (Array.isArray(children)) {
      text = children.map(extractTextFromElement).join(" "); // Add space between array items
    } else if (children) {
      text = extractTextFromElement(children);
    }
  } else if (Array.isArray(element)) {
    text = element.map(extractTextFromElement).join(" "); // Add space between array items
  } else if (element) {
    text = String(element);
  }

  // Clean up the text by removing HTML tags and common entities
  return text
    .replace(/<[^>]*>/g, " ") // Replace HTML tags with space
    .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
    .replace(/&/g, " & ") // Add spaces around ampersands
    .replace(/\s+/g, " ") // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing spaces
};

export const isBrowser = typeof window !== "undefined";

const uuidv4 = () => {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16),
  );
};

export const getUuid = () => {
  // Check if the uuid is already in local storage
  if (typeof window !== "undefined") {
    const uuid = localStorage.getItem("uuid");
    if (uuid) {
      return uuid;
    }

    // if not, generate a new uuid
    const uuid_v4 = uuidv4();
    // save the uuid to local storage
    localStorage.setItem("uuid", uuid_v4);
    return uuid_v4;
  }
};

/**
 * Module-scope cache of utility-nav height — measured while it's visible,
 * used for upward-scroll offset calculations when it's currently hidden.
 */
let cachedUtilityNavHeight = 0;

/**
 * Computes scroll offset matching the header's FINAL state after a smooth scroll:
 *   - scrolling down → utility-nav will be hidden (.header--hidden)
 *   - scrolling up   → utility-nav will be visible
 * Adds the sticky PageNav height when present.
 */
const computeHeaderAwareOffset = (el) => {
  if (typeof document === "undefined") return 0;
  const header = document.querySelector("header");
  if (!header) return 0;

  const targetDocY = el.getBoundingClientRect().top + window.scrollY;
  const scrollingDown = targetDocY > window.scrollY;

  const isUtilityHidden = header.classList.contains("header--hidden");
  const headerHeight = header.offsetHeight || 0;
  const utilityNav = header.querySelector(".utility-nav");
  const liveUtilityHeight = utilityNav?.offsetHeight || 0;
  if (liveUtilityHeight > 0) cachedUtilityNavHeight = liveUtilityHeight;
  const utilityHeight = liveUtilityHeight || cachedUtilityNavHeight;

  const headerNoUtility = isUtilityHidden
    ? headerHeight
    : headerHeight - utilityHeight;
  const headerWithUtility = isUtilityHidden
    ? headerHeight + utilityHeight
    : headerHeight;

  const effectiveHeader = scrollingDown ? headerNoUtility : headerWithUtility;

  const pageNav =
    document.querySelector(".innerpage-nav-container") ||
    document.querySelector(".innerpage-nav");
  const navHeight = pageNav?.offsetHeight || 0;

  return Math.max(0, effectiveHeader + navHeight);
};

/**
 * When offset is 0, auto-computes a header-aware offset so anchor scrolls land
 * below the header's FINAL state (utility-nav hides on scroll-down, shows on up).
 * Preemptively forces the utility-nav to its final visibility so the header
 * doesn't collapse/expand mid-scroll (which causes the sticky PageNav to jump).
 */
export const scrollToSection = (selector, offset = 0) => {
  if (typeof window === "undefined") return;

  const el = document.querySelector(selector);
  if (!el) return;

  const notifyComplete = () => {
    window.dispatchEvent(new CustomEvent("smooth-scroll-complete"));
  };

  const targetDocY = el.getBoundingClientRect().top + window.scrollY;
  const scrollingDown = targetDocY > window.scrollY;

  const effectiveOffset =
    offset !== 0 ? offset : computeHeaderAwareOffset(el);

  // For upward anchor scrolls, signal the header to stay visible so a transient
  // "down" detection by the scroll-direction hook (mobile bounce, settling event)
  // doesn't hide the utility-nav after the user lands at the section.
  if (!scrollingDown) {
    window.dispatchEvent(
      new CustomEvent("anchor-keep-header-visible", { detail: { ms: 2000 } }),
    );
  }

  const scrollToTarget = () => {
    const rect = el.getBoundingClientRect();
    const y = rect.top + window.scrollY - effectiveOffset;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(scrollToTarget);
  });

  setTimeout(notifyComplete, 1000);
};
